import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Table,
  Card,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Pagination,
} from "react-bootstrap";
import {
  FaEdit,
  FaPlus,
  FaSearch,
  FaToggleOn,
  FaToggleOff,
  FaCheckCircle,
  FaTimesCircle,
  FaStickyNote,
} from "react-icons/fa";

const API_URL = "https://mixta39.dev/phpapi/api/profesores.php"; // profesores
const ANOTACIONES_API_URL = "https://mixta39.dev/phpapi/api/profesoresanotaciones.php"; // anotaciones

// ===== Helpers de fecha (LOCAL) =====
function todayLocalYYYYMMDD() {
  const d = new Date(); // zona local del usuario
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Si alguna vez necesitas crear Date desde "YYYY-MM-DD" (date-only), evita UTC implícito:
function parseDateOnlyToLocal(dateOnly) {
  const [y, m, d] = dateOnly.split("-").map(Number);
  return new Date(y, m - 1, d); // interpreta como local
}

const Profesores = () => {
  const navigate = useNavigate();
  const [profesores, setProfesores] = useState([]);
  const [filteredProfesores, setFilteredProfesores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAnotacionesModal, setShowAnotacionesModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [profesoresPerPage] = useState(20);
  const [successMessage, setSuccessMessage] = useState("");
  const [nuevoProfesor, setNuevoProfesor] = useState({
    Nombre: "",
    ApellidoPaterno: "",
    ApellidoMaterno: "",
    TelefonoDeContacto: "",
    CURP: "",
    Email: "",
    EsActivo: true,
    UsuarioCreadorID: 2,
  });
  const [error, setError] = useState("");
  const [editingProfesor, setEditingProfesor] = useState(null);

  // Estados para anotaciones
  const [anotaciones, setAnotaciones] = useState("");
  const [fechaAnotacion, setFechaAnotacion] = useState(todayLocalYYYYMMDD());
  const [profesorSeleccionado, setProfesorSeleccionado] = useState(null);
  const [anotacionesError, setAnotacionesError] = useState("");
  const [savingAnotacion, setSavingAnotacion] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("user");
    if (!token) {
      navigate("/login");
    } else {
      fetchProfesores();
    }
  }, [navigate]);

  const fetchProfesores = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_URL, { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error("Error en la respuesta del servidor");
      const data = await response.json();
      setProfesores(data);
      setFilteredProfesores(data);
    } catch (err) {
      console.error("Error al cargar los profesores:", err);
      setError("No se pudieron cargar los profesores.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filtered = profesores.filter((profesor) =>
      `${profesor.Nombre} ${profesor.ApellidoPaterno} ${profesor.ApellidoMaterno} ${profesor.CURP} ${profesor.Email}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
    setFilteredProfesores(filtered);
    setCurrentPage(1);
  }, [searchTerm, profesores]);

  const indexOfLastProfesor = currentPage * profesoresPerPage;
  const indexOfFirstProfesor = indexOfLastProfesor - profesoresPerPage;
  const currentProfesores = filteredProfesores.slice(
    indexOfFirstProfesor,
    indexOfLastProfesor
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredProfesores.length / profesoresPerPage); i++) {
    pageNumbers.push(i);
  }

  const handleShow = () => {
    setNuevoProfesor({
      Nombre: "",
      ApellidoPaterno: "",
      ApellidoMaterno: "",
      TelefonoDeContacto: "",
      CURP: "",
      Email: "",
      EsActivo: true,
      UsuarioCreadorID: 2,
    });
    setError("");
    setEditingProfesor(null);
    setShowModal(true);
  };

  const handleEdit = (profesor) => {
    setNuevoProfesor(profesor);
    setEditingProfesor(profesor);
    setError("");
    setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  const handleChange = (e) => {
    setNuevoProfesor({ ...nuevoProfesor, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (Object.values(nuevoProfesor).some((value) => value === "")) {
      setError("Todos los campos son obligatorios.");
      return;
    }

    try {
      const method = editingProfesor ? "PUT" : "POST";
      const url = editingProfesor ? `${API_URL}/${editingProfesor.ProfesorID}` : API_URL;

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(nuevoProfesor),
      });

      const data = await response.json();
      if (response.ok && data?.success) {
        fetchProfesores();
        setSuccessMessage(
          editingProfesor ? "Profesor actualizado correctamente." : "Profesor agregado correctamente."
        );
        setTimeout(() => setSuccessMessage(""), 3000);
        handleClose();
      } else {
        setError(data?.error || data?.message || "Error al guardar el profesor.");
      }
    } catch (err) {
      setError("Error de conexión con el servidor.");
      console.error("Error al guardar:", err);
    }
  };

  const toggleEstadoProfesor = async (profesor) => {
    const updatedProfesor = { ...profesor, EsActivo: profesor.EsActivo ? 0 : 1 };
    try {
      const response = await fetch(`${API_URL}/${profesor.ProfesorID}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(updatedProfesor),
      });

      const data = await response.json();
      if (response.ok && data?.success) {
        fetchProfesores();
      } else {
        console.error("Error al actualizar el estado:", data?.error || data?.message);
      }
    } catch (err) {
      console.error("Error de conexión con el servidor:", err);
    }
  };

  // Abrir modal de anotaciones
  const handleAnotaciones = (profesor) => {
    setProfesorSeleccionado(profesor);
    setAnotaciones("");
    setAnotacionesError("");
    setFechaAnotacion(todayLocalYYYYMMDD()); // <- LOCAL
    setShowAnotacionesModal(true);
  };

  // Guardar anotaciones (manejo robusto de la respuesta)
  const handleSaveAnotaciones = async () => {
    setAnotacionesError("");

    if (!anotaciones.trim()) {
      setAnotacionesError("La anotación no puede estar vacía.");
      return;
    }

    const today = todayLocalYYYYMMDD(); // <- LOCAL
    if (fechaAnotacion > today) {
      setAnotacionesError("La fecha de la anotación no puede ser mayor al día de hoy.");
      return;
    }

    if (!profesorSeleccionado?.ProfesorID) {
      setAnotacionesError("Profesor no válido.");
      return;
    }

    // Obtener el usuario logueado del localStorage
    let usuarioCreadorID = null;
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        usuarioCreadorID =
          parsedUser?.UsuarioID ?? parsedUser?.usuarioId ?? parsedUser?.id ?? null;
      }
    } catch (e) {
      console.error("Error al obtener el usuario logueado:", e);
    }

    if (!usuarioCreadorID) {
      setAnotacionesError("No se pudo obtener el usuario logueado.");
      return;
    }

    setSavingAnotacion(true);
    try {
      const body = {
        ProfesorID: profesorSeleccionado.ProfesorID,
        FechaAnotacion: fechaAnotacion, // "YYYY-MM-DD"
        Anotacion: anotaciones,
        UsuarioCreadorID: usuarioCreadorID,
      };

      const response = await fetch(ANOTACIONES_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(body),
      });

      // Leer una sola vez y tratar de parsear
      const raw = await response.text();
      let json = null;
      try {
        json = raw ? JSON.parse(raw) : null;
      } catch {
        /* cuerpo no es JSON válido */
      }

      const success = response.ok && json?.success === true;

      if (!success) {
        const msg = json?.error || json?.message || raw || "Error al guardar la anotación.";
        setAnotacionesError(msg);
        return;
      }

      // Éxito
      setShowAnotacionesModal(false);
      setSuccessMessage(json?.message || "Anotación guardada correctamente.");
      setTimeout(() => setSuccessMessage(""), 3000);
      fetchProfesores();
    } catch (err) {
      console.error("Error al guardar la anotación:", err);
      setAnotacionesError("Error de conexión con el servidor.");
    } finally {
      setSavingAnotacion(false);
    }
  };

  return (
    <Container className="mt-5">
      <Card className="shadow p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="text-center">Lista de Profesores</h2>
          <Button variant="success" onClick={handleShow}>
            <FaPlus /> Agregar Profesor
          </Button>
        </div>

        <div className="mb-3 d-flex align-items-center">
          <FaSearch className="me-2" />
          <Form.Control
            type="text"
            placeholder="Buscar por nombre, apellido, CURP o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {successMessage && (
          <Alert variant="success">
            <FaCheckCircle className="me-2" /> {successMessage}
          </Alert>
        )}

        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
            <p>Cargando profesores...</p>
          </div>
        ) : (
          <>
            <Table striped bordered hover responsive className="table table-hover">
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Nombre Completo</th>
                  <th>Teléfono</th>
                  <th>CURP</th>
                  <th>Email</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentProfesores.map((profesor) => (
                  <tr key={profesor.ProfesorID}>
                    <td>{profesor.ProfesorID}</td>
                    <td>{`${profesor.Nombre} ${profesor.ApellidoPaterno} ${profesor.ApellidoMaterno}`}</td>
                    <td>{profesor.TelefonoDeContacto || "Sin número"}</td>
                    <td>{profesor.CURP}</td>
                    <td>{profesor.Email}</td>
                    <td className="text-center">
                      {profesor.EsActivo ? (
                        <FaCheckCircle className="text-success" />
                      ) : (
                        <FaTimesCircle className="text-danger" />
                      )}
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(profesor)}
                      >
                        <FaEdit />
                      </Button>
                      &nbsp;
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => toggleEstadoProfesor(profesor)}
                      >
                        {profesor.EsActivo ? <FaToggleOn /> : <FaToggleOff />}
                      </Button>
                      &nbsp;
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => handleAnotaciones(profesor)}
                      >
                        <FaStickyNote />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <Pagination className="justify-content-center">
              {pageNumbers.map((number) => (
                <Pagination.Item
                  key={number}
                  onClick={() => paginate(number)}
                  active={number === currentPage}
                >
                  {number}
                </Pagination.Item>
              ))}
            </Pagination>
          </>
        )}
      </Card>

      <br />

      {/* Modal para agregar/editar profesor */}
      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{editingProfesor ? "Editar Profesor" : "Agregar Profesor"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form>
            {[
              "Nombre",
              "ApellidoPaterno",
              "ApellidoMaterno",
              "TelefonoDeContacto",
              "CURP",
              "Email",
            ].map((field) => (
              <Form.Group key={field} className="mb-3">
                <Form.Label>
                  {field.replace(/([A-Z])/g, " $1")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name={field}
                  value={nuevoProfesor[field]}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            ))}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para anotaciones */}
      <Modal show={showAnotacionesModal} onHide={() => setShowAnotacionesModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Anotaciones - {profesorSeleccionado?.Nombre} {profesorSeleccionado?.ApellidoPaterno}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {anotacionesError && <Alert variant="danger">{anotacionesError}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Fecha de la anotación:</Form.Label>
              <Form.Control
                type="date"
                value={fechaAnotacion}
                max={todayLocalYYYYMMDD()}        // <- LOCAL
                onChange={(e) => setFechaAnotacion(e.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Escribe las anotaciones:</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={anotaciones}
                onChange={(e) => setAnotaciones(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAnotacionesModal(false)}>
            Cerrar
          </Button>
          <Button variant="primary" onClick={handleSaveAnotaciones} disabled={savingAnotacion}>
            {savingAnotacion ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />{" "}
                Guardando...
              </>
            ) : (
              "Guardar"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Profesores;
